import { Button } from "@mui/material";
import { alpha } from "@mui/material";
import { COLOR_BLACK, COLOR_GREY_BORDER, COLOR_WHITE } from "helpers/colors";
import { FONT_FAMILY_GRAPHIK } from "helpers/FONT";
import { SPACING, SPACING_HALF } from "helpers/spacings";
import StatsTable, {
  StatsTableButtonRow,
  StatsTableCell,
  StatsTableHeaderCell,
  StatsTableRow,
} from "molecules/StatsTable";
import React, { useMemo, useState } from "react";

// Points segment columns to skip entirely
const POINTS_SKIP = new Set(["FPG AVG"]);

// Points segment columns moved to end of unified table (after other-segment columns)
const POINTS_COLS_LAST = new Set(["Fouls", "Games"]);

// Display name overrides for Points segment simple columns
const POINTS_DISPLAY = { Total: "PTS", Fouls: "PF", Games: "GP" };

// Display name for first column of each non-Points segment
const SEGMENT_FIRST_COL_DISPLAY = {
  Assists: "AST",
  Rebounds: "REB",
  Steals: "STL",
  Blocks: "BLK",
  Turnovers: "TO",
};

const SEGMENT_ORDER = ["Assists", "Rebounds", "Steals", "Blocks", "Turnovers"];

function getPercentageLabels(shortName) {
  const s = shortName.toUpperCase();
  if (s.includes("FGM") || (s.startsWith("FG") && !s.startsWith("FT"))) return ["FG-M", "FG-A", "FG%"];
  if (s.includes("3P")) return ["3PM", "3PA", "3PT%"];
  if (s.includes("FTM") || s.includes("FT")) return ["FTM", "FTA", "FT%"];
  return [`${shortName}-M`, `${shortName}-A`, `${shortName}%`];
}

function buildColumnsAndRows(segments) {
  if (!segments?.length) return { columns: [], mergedRows: [], headColumns: [] };

  const segMap = {};
  for (const seg of segments) segMap[seg.title] = seg;

  const pointsSeg = segMap["Points"] || segments[0];
  const sampleRow = pointsSeg.rows[0];

  const colsFirst = [];
  const colsMiddle = [];
  const colsLast = [];

  // Points segment columns
  for (let i = 0; i < pointsSeg.columns.length; i++) {
    const col = pointsSeg.columns[i];
    if (POINTS_SKIP.has(col.shortName)) continue;

    const cellKey = `Points:${col.shortName}`;
    const isLast = POINTS_COLS_LAST.has(col.shortName);
    const target = isLast ? colsLast : colsFirst;
    const sampleCell = sampleRow?.cells[i];

    if (sampleCell?.statType === "percentage") {
      const [mLabel, aLabel, pLabel] = getPercentageLabels(col.shortName);
      target.push({
        key: `${col.shortName}_made`,
        label: mLabel,
        fullName: `${col.shortName} Made`,
        cellKey,
        sortValue: (cell) => cell?.made ?? -Infinity,
        renderCell: (cell) => (cell?.made != null ? cell.made : "—"),
        isDefaultSort: false,
      });
      target.push({
        key: `${col.shortName}_total`,
        label: aLabel,
        fullName: `${col.shortName} Attempted`,
        cellKey,
        sortValue: (cell) => cell?.total ?? -Infinity,
        renderCell: (cell) => (cell?.total != null ? cell.total : "—"),
        isDefaultSort: false,
      });
      target.push({
        key: `${col.shortName}_pct`,
        label: pLabel,
        fullName: `${col.shortName} Percentage`,
        cellKey,
        sortValue: (cell) => (cell?.total > 0 ? cell.made / cell.total : -Infinity),
        renderCell: (cell) => (cell?.total > 0 ? `${Math.round((cell.made / cell.total) * 100)}%` : "—"),
        isDefaultSort: false,
      });
    } else {
      target.push({
        key: col.shortName,
        label: POINTS_DISPLAY[col.shortName] || col.shortName,
        fullName: col.name || col.shortName,
        cellKey,
        sortValue: (cell) => parseFloat(cell?.value) || 0,
        renderCell: (cell) => cell?.value ?? "0",
        isDefaultSort: col.shortName === "PPG",
      });
    }
  }

  // First column from each other segment (in defined order)
  for (const segTitle of SEGMENT_ORDER) {
    const seg = segMap[segTitle];
    if (seg?.columns?.length > 0) {
      const col = seg.columns[0];
      const cellKey = `${segTitle}:${col.shortName}`;
      colsMiddle.push({
        key: `${segTitle}_0`,
        label: SEGMENT_FIRST_COL_DISPLAY[segTitle] || col.shortName,
        fullName: col.name || col.shortName,
        cellKey,
        sortValue: (cell) => parseFloat(cell?.value) || 0,
        renderCell: (cell) => cell?.value ?? "0",
        isDefaultSort: false,
      });
    }
  }

  const allColumns = [...colsFirst, ...colsMiddle, ...colsLast];

  // Build player map — keyed by headCells joined (player + team)
  const playerMap = new Map();

  for (const row of pointsSeg.rows) {
    const key = row.headCells.join("|");
    const cellsByKey = {};
    pointsSeg.columns.forEach((col, i) => {
      cellsByKey[`Points:${col.shortName}`] = row.cells[i];
    });
    playerMap.set(key, {
      headCells: row.headCells,
      highlighted: row.highlighted,
      collapsible: row.collapsible,
      cellsByKey,
    });
  }

  for (const seg of segments) {
    if (seg.title === pointsSeg.title) continue;
    for (const row of seg.rows) {
      const key = row.headCells.join("|");
      if (!playerMap.has(key)) {
        playerMap.set(key, {
          headCells: row.headCells,
          highlighted: row.highlighted,
          collapsible: row.collapsible,
          cellsByKey: {},
        });
      }
      const entry = playerMap.get(key);
      seg.columns.forEach((col, i) => {
        entry.cellsByKey[`${seg.title}:${col.shortName}`] = row.cells[i];
      });
    }
  }

  return {
    columns: allColumns,
    mergedRows: Array.from(playerMap.values()),
    headColumns: pointsSeg.headColumns || [],
  };
}

// Rows ranked beyond this position are hidden behind a "Show More" toggle.
const COLLAPSE_THRESHOLD = 20;

export default function UnifiedLeaderboardTable({ segments, title }) {
  const { columns, mergedRows, headColumns } = useMemo(() => buildColumnsAndRows(segments), [segments]);

  // Default sorting is PPG - Points per Game by descending
  const defaultSortKey = columns.find((c) => c.isDefaultSort)?.key ?? columns[0]?.key ?? "PPG";
  const [sortKey, sortKeySet] = useState("PPG");
  const [sortDir, sortDirSet] = useState("desc");
  const [collapsed, collapsedSet] = useState(true);

  const effectiveSortKey = columns.find((c) => c.key === sortKey) ? sortKey : defaultSortKey;
  const sortCol = columns.find((c) => c.key === effectiveSortKey);

  const sortedRows = useMemo(() => {
    if (!sortCol) return mergedRows;
    return [...mergedRows].sort((a, b) => {
      const av = sortCol.sortValue(a.cellsByKey[sortCol.cellKey]);
      const bv = sortCol.sortValue(b.cellsByKey[sortCol.cellKey]);
      return sortDir === "desc" ? bv - av : av - bv;
    });
  }, [mergedRows, sortCol, sortDir]);

  // Dense ranking: same value → same rank
  const rankedRows = useMemo(() => {
    let rank = 1;
    return sortedRows.map((row, i) => {
      if (i > 0 && sortCol) {
        const prev = sortCol.sortValue(sortedRows[i - 1].cellsByKey[sortCol.cellKey]);
        const cur = sortCol.sortValue(row.cellsByKey[sortCol.cellKey]);
        if (prev !== cur) rank = i + 1;
      }
      return { ...row, rank };
    });
  }, [sortedRows, sortCol]);

  // Collapse by the current sort order: keep the top rows (plus the highlighted user row) when collapsed.
  const hasCollapsible = rankedRows.length > COLLAPSE_THRESHOLD;
  const visibleRows =
    collapsed && hasCollapsible ? rankedRows.filter((row, i) => i < COLLAPSE_THRESHOLD || row.highlighted) : rankedRows;

  const handleSort = (key) => {
    if (key === effectiveSortKey) {
      sortDirSet((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      sortKeySet(key);
      sortDirSet("desc");
    }
  };

  return (
    <div style={{ display: "flex", flexFlow: "column nowrap", gap: SPACING }}>
      {title && (
        <div
          style={{
            paddingBottom: SPACING_HALF,
            borderBottom: `1px solid ${COLOR_GREY_BORDER}`,
            fontFamily: FONT_FAMILY_GRAPHIK,
            fontWeight: 700,
            fontSize: 16,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            color: COLOR_BLACK,
          }}
        >
          {title}
        </div>
      )}

      <StatsTable
        headerColumns={["RNK", ...headColumns]}
        columns={columns.map((col) => {
          const isActive = col.key === effectiveSortKey;
          return (
            <button
              key={col.key}
              title={col.fullName}
              onClick={() => handleSort(col.key)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: isActive ? COLOR_WHITE : alpha(COLOR_WHITE, 0.6),
                fontFamily: FONT_FAMILY_GRAPHIK,
                fontWeight: isActive ? 600 : 500,
                fontSize: "inherit",
                textTransform: "uppercase",
                padding: "0 4px",
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                whiteSpace: "nowrap",
              }}
            >
              {col.label}
              <span
                style={{
                  fontSize: "0.75em",
                  lineHeight: 1,
                  color: COLOR_WHITE,
                  visibility: isActive ? "visible" : "hidden",
                }}
              >
                {sortDir === "desc" ? "▼" : "▲"}
              </span>
            </button>
          );
        })}
        color={alpha(COLOR_BLACK, 0)}
      >
        {visibleRows.map((row, rowIndex) => (
          // NOTE: row.highlighted is calculated in LeaderboardTableSegment graphql. But we decide not to show row as highlighted in new leaderboard.
          <StatsTableRow key={row.headCells.join("|")} rowIndex={rowIndex}>
            <StatsTableHeaderCell>{row.rank}</StatsTableHeaderCell>
            {row.headCells.map((cell, i) => (
              <StatsTableHeaderCell key={i}>{cell}</StatsTableHeaderCell>
            ))}
            {columns.map((col) => (
              <StatsTableCell key={col.key}>{col.renderCell(row.cellsByKey[col.cellKey])}</StatsTableCell>
            ))}
          </StatsTableRow>
        ))}
        {hasCollapsible && (
          <StatsTableButtonRow>
            <Button
              fullWidth
              color="inherit"
              onClick={() => collapsedSet(!collapsed)}
              style={{
                textTransform: "uppercase",
                fontFamily: FONT_FAMILY_GRAPHIK,
                fontWeight: 600,
                fontSize: 10,
              }}
            >
              {collapsed ? "Show More" : "Show Less"}
            </Button>
          </StatsTableButtonRow>
        )}
      </StatsTable>
    </div>
  );
}
